/**
 * The little black book on screen: a worn leather notebook opened over the game.
 *
 *  - HOME (the index page) comes first: a "Right now" slip with the one thing that matters most (the job
 *    you are on, an invite, what to do next) and its main button, then a grid of index tiles — one per
 *    section, each with a line icon, a label and a live line ("2 new", "£1,240 in the bank"). Online a
 *    second group has a tile per street panel (pockets, chat, freight…) that opens the panel straight away.
 *  - A tile opens its SECTION as a full page: "‹ Book" and the section's title on the leather, the list on
 *    the left page and the chosen entry on the right with its buttons pinned to the foot.
 *
 * Keyboard / pad: on HOME the arrows move between the slip's buttons and the tiles, Enter opens, 1–9 open a
 * tile. In a section ↑ ↓ choose an entry, ← → turn to the neighbouring section, Enter does the entry's main
 * thing. Esc / Backspace / pad B go back where you came from (a section opened from HOME goes back to HOME;
 * one opened straight from the game shuts the book); I always shuts it. Touch and mouse: tap; ✕ or a tap on
 * the dimmed game behind shuts it.
 *
 * It only draws: what is on each page comes from the `BookPages` the book system hands it.
 */
import { renderLadder, type BookLadder } from './contactLadder';
import { pastePhoto, portraitId } from '../ui/art';
import { applyIcon, type IconId } from '../ui/mapIcons';
import { el, esc, html, scrollCue, toggle } from '../ui/dom';
import type { NavAction } from '../ui/screens';
import type { ModuleOverlay } from '../ui/uiTypes';
import { BOOK_ICONS, type BookIcon } from './bookIcons';
import { homeNav } from './bookNav';

/** `online` is the offline book's one page for what only the shared city has (gangs, crews); online it is GANG, CREW and STREET. */
export type BookTab = 'jobs' | 'messages' | 'contacts' | 'money' | 'motor' | 'record' | 'week' | 'trade' | 'hideout' | 'gang' | 'crew' | 'street' | 'online' | 'map' | 'help' | 'settings';
/** What the book shows: its HOME index or one section. */
export type BookView = BookTab | 'home';

export const TAB_LABELS: Record<BookTab, string> = {
  jobs: 'Jobs',
  messages: 'Messages',
  contacts: 'Contacts',
  money: 'Money',
  motor: 'Your motor',
  record: 'Your record',
  week: 'The week',
  hideout: 'Hideout',
  trade: 'Wilf’s book',
  gang: 'Gang',
  crew: 'Crew',
  street: 'Street',
  online: 'Online',
  map: 'Map',
  help: 'How to',
  settings: 'Settings',
};

export const TAB_ICONS: Record<BookTab, BookIcon> = {
  jobs: 'jobs',
  messages: 'messages',
  contacts: 'contacts',
  money: 'money',
  motor: 'motor',
  record: 'record',
  week: 'week',
  hideout: 'hideout',
  trade: 'trade',
  gang: 'gang',
  crew: 'crew',
  street: 'street',
  online: 'online',
  map: 'map',
  help: 'help',
  settings: 'settings',
};

export interface BookButton {
  label: string;
  /** The small line under the label: the place for "Mark on the map" ("Phone Box, The Parade"). */
  sub?: string;
  run(): void;
  /** Keyboard letter shown on the button (desktop). */
  key?: string;
  /** Extra classes / data attributes (the online grid keeps the MENU panel's classes). */
  cls?: string;
  data?: Record<string, string>;
}

export interface BookEntry {
  id: string;
  title: string;
  sub?: string;
  /**
   * A ruled heading written above this entry on the left page ("Work going", "Later"). Entries that share
   * a section sit under one heading; ↑ ↓ skip the headings.
   */
  section?: string;
  /** A little stamp in the margin. */
  mark?: 'new' | 'done' | 'copper' | 'clean' | 'proper' | 'locked' | 'active' | 'online' | 'offline' | 'here';
  /** Letter key (desktop hotkeys). */
  key?: string;
  /** Right page. */
  kicker?: string;
  heading?: string;
  body?: string[];
  meta?: string;
  actions?: BookButton[];
  /** A person's name: their portrait as a small photo beside the line on the left page (docs/UI.md, Painted art). */
  avatar?: string;
  /** A person's name: their portrait pasted on the right page, `large` (CONTACTS) or `note` (a message). */
  photo?: { name: string; size: 'large' | 'note' };
  /** A contact's ladder (WP-X1), drawn on the right page above the body. */
  ladder?: BookLadder;
  /** What their place is, as its map icon (src/ui/mapIcons.ts) before the sub line and the kicker. */
  icon?: IconId;
  /** Custom right page (replaces body / actions). */
  render?(page: HTMLElement): void;
  /** Called when the entry is chosen (messages mark themselves read). */
  onSelect?(): void;
}

export interface BookPage {
  /** The section's title (on the leather above the pages) and a line under it. */
  heading: string;
  lede?: string;
  entries: BookEntry[];
  /** Shown on the right page when there are no entries. */
  empty?: string;
}

/** One index tile on HOME: a section of the book (`tab`) or a straight shortcut (`run`, an online panel). */
export interface BookTile {
  id: string;
  label: string;
  icon: BookIcon;
  /** The live line under the label ("2 new", "Job running: Wet Paper"). */
  status?: string;
  /** A count / word in the red badge on the corner ("2", "!"). */
  badge?: string;
  /** The tile wants you (an invite, rent owing, a job on): the status line is inked red. */
  alert?: boolean;
  tab?: BookTab;
  run?(): void;
}

export interface BookTileGroup {
  id: string;
  /** A ruled heading above the group ("On the street"); none for the book's own sections. */
  heading?: string;
  /** A link at the end of the heading ("Every panel ›" → STREET). */
  more?: { label: string; tab: BookTab };
  tiles: BookTile[];
}

/** The "Right now" slip at the top of HOME: the one thing that matters most, with its main button first. */
export interface BookNow {
  kicker: string;
  title: string;
  sub?: string;
  /** Colour of the slip: a job on (oxblood), something waiting for an answer (mustard), a calm suggestion (ink). */
  tone: 'job' | 'invite' | 'next' | 'calm';
  avatar?: string;
  /** A tap on the slip itself (not a button) opens this section, on this entry. */
  tab?: BookTab;
  focusId?: string;
  actions: BookButton[];
}

export interface BookHome {
  now: BookNow | null;
  groups: BookTileGroup[];
}

export interface BookPages {
  tabs(): BookTab[];
  page(tab: BookTab): BookPage;
  home(): BookHome;
  /** Unread count (the MESSAGES tile's badge). */
  unread(): number;
  touch(): boolean;
  sound(s: 'tick' | 'click' | 'confirm' | 'back' | 'whoosh' | 'denied'): void;
  close(): void;
}

const MARK_TEXT: Record<NonNullable<BookEntry['mark']>, string> = {
  new: 'NEW',
  done: 'DONE',
  copper: 'DONE',
  clean: 'CLEAN',
  proper: 'PROPER',
  locked: 'LATER',
  active: 'ON NOW',
  online: 'ABOUT',
  offline: 'AWAY',
  here: 'GO',
};

const FOOT_HOME = '<span>Arrows choose</span><span>Enter open</span><span>1–9 a tile</span><span>Esc / I shut</span>';
const FOOT_SECTION = '<span>↑ ↓ choose</span><span>Enter do it</span><span>← → next section</span><span>Esc back</span><span>I shut</span>';

export class BookScreen implements ModuleOverlay {
  readonly root: HTMLElement;
  /** What is showing: HOME or a section. */
  view: BookView;
  /** The section showing (or last shown). */
  tab: BookTab;
  /** The chosen entry in the section. */
  index = 0;
  /** The focused slip button / tile on HOME (keyboard and pad). */
  homeIndex = 0;
  /** This section was opened from HOME: back goes back there (opened straight from the game, back shuts the book). */
  private fromHome = false;
  private readonly backEl: HTMLButtonElement;
  private readonly titleEl: HTMLElement;
  private readonly homeEl: HTMLElement;
  private readonly spreadEl: HTMLElement;
  private readonly leftEl: HTMLElement;
  private readonly rightEl: HTMLElement;
  private readonly footEl: HTMLElement | null;
  private entries: BookEntry[] = [];
  private listEls: HTMLElement[] = [];
  /** HOME's focusable things in reading order (the slip's buttons, then every tile) and the tiles alone (1–9). */
  private homeItems: HTMLElement[] = [];
  private tileEls: HTMLElement[] = [];
  /** Scroll cues (a torn-edge fade at the foot of a page with more to read): re-measured after each redraw. */
  private readonly cueHome: () => void;
  private readonly cueLeft: () => void;
  private readonly cueRight: () => void;

  constructor(parent: HTMLElement, private readonly pages: BookPages, view: BookView, focusId?: string) {
    this.view = view;
    this.tab = view === 'home' ? 'jobs' : view;
    this.root = el('div', 'bh-screen bk-screen', undefined, parent);
    toggle(this.root, 'touch', pages.touch());
    this.root.addEventListener('click', (e) => {
      if (e.target === this.root) {
        pages.sound('back');
        pages.close();
      }
    });
    const book = el('div', 'bk-book', undefined, this.root);
    book.setAttribute('role', 'dialog');
    book.setAttribute('aria-label', 'Your little black book');
    const head = el('div', 'bk-head', undefined, book);
    this.backEl = el('button', 'bk-back', undefined, head) as HTMLButtonElement;
    this.backEl.type = 'button';
    html('span', 'chev', '‹', this.backEl);
    el('span', 'lbl', 'Book', this.backEl);
    this.backEl.setAttribute('aria-label', 'Back to the book’s index');
    this.backEl.addEventListener('click', (e) => {
      e.stopPropagation();
      this.pages.sound('back');
      this.goHome();
    });
    this.titleEl = el('div', 'bk-title', undefined, head);
    const close = el('button', 'bk-close', '✕', head);
    close.type = 'button';
    close.setAttribute('aria-label', 'Shut the book');
    close.addEventListener('click', (e) => {
      e.stopPropagation();
      pages.sound('back');
      pages.close();
    });
    this.homeEl = el('div', 'bk-home', undefined, book);
    this.homeEl.dataset.touchScroll = '';
    this.spreadEl = el('div', 'bk-spread', undefined, book);
    this.leftEl = el('div', 'bk-page left', undefined, this.spreadEl);
    this.rightEl = el('div', 'bk-page right', undefined, this.spreadEl);
    this.leftEl.dataset.touchScroll = '';
    this.rightEl.dataset.touchScroll = '';
    this.cueHome = scrollCue(this.homeEl);
    this.cueLeft = scrollCue(this.leftEl);
    this.cueRight = scrollCue(this.rightEl);
    this.footEl = pages.touch() ? null : el('div', 'bk-foot', undefined, book);
    if (view === 'home') this.renderHome();
    else this.show(view, true, focusId);
  }

  // ------------------------------------------------------------------ HOME

  /** Back to the index page. */
  goHome(): void {
    this.view = 'home';
    this.fromHome = false;
    this.renderHome();
  }

  private setChrome(): void {
    const home = this.view === 'home';
    this.root.dataset.view = home ? 'home' : 'section';
    this.root.dataset.tab = this.view;
    this.backEl.hidden = home;
    this.homeEl.hidden = !home;
    this.spreadEl.hidden = home;
    if (this.footEl) this.footEl.innerHTML = home ? FOOT_HOME : FOOT_SECTION;
  }

  private renderHome(): void {
    this.setChrome();
    this.titleEl.textContent = '';
    html('span', 'bk-emboss', 'Little&nbsp;Black&nbsp;Book', this.titleEl);
    const home = this.pages.home();
    const h = this.homeEl;
    h.textContent = '';
    this.homeItems = [];
    this.tileEls = [];
    // Two groups (online: the book and the street panels) or a lot of tiles: compact rows, so it all fits.
    toggle(h, 'dense', home.groups.filter((g) => g.tiles.length).length > 1 || home.groups.reduce((n, g) => n + g.tiles.length, 0) > 12);
    if (home.now) this.renderNow(home.now);
    for (const g of home.groups) {
      if (!g.tiles.length) continue;
      const sec = el('section', 'bk-group', undefined, h);
      sec.dataset.group = g.id;
      if (g.heading || g.more) {
        const gh = el('div', 'bk-gh', undefined, sec);
        el('span', '', g.heading ?? '', gh);
        if (g.more) {
          const more = g.more;
          const m = el('button', 'bk-more', more.label, gh);
          m.type = 'button';
          m.dataset.tile = more.tab;
          m.addEventListener('click', (e) => {
            e.stopPropagation();
            this.pages.sound('tick');
            this.openSection(more.tab);
          });
        }
      }
      const grid = el('div', 'bk-tiles', undefined, sec);
      for (const t of g.tiles) {
        const b = el('button', `bk-tile${t.alert ? ' alert' : ''}${t.run ? ' direct' : ''}`, undefined, grid);
        b.type = 'button';
        b.dataset.tile = t.id;
        const n = this.tileEls.length + 1;
        if (n <= 9 && !this.pages.touch()) el('kbd', '', String(n), b);
        html('span', 'ic', BOOK_ICONS[t.icon], b);
        const tw = el('span', 'tw', undefined, b);
        el('span', 'lb', t.label, tw);
        if (t.status) el('span', 'st', t.status, tw);
        if (t.badge) el('b', 'badge', t.badge, b);
        b.setAttribute('aria-label', [t.label, t.badge ? `${t.badge} new` : '', t.status ?? ''].filter(Boolean).join(', '));
        b.addEventListener('click', (e) => {
          e.stopPropagation();
          this.runTile(t);
        });
        this.tileEls.push(b);
        this.homeItems.push(b);
      }
    }
    this.homeIndex = Math.max(0, Math.min(this.homeItems.length - 1, this.homeIndex));
    this.focusHome(this.homeIndex, false);
    this.cueHome();
  }

  private renderNow(now: BookNow): void {
    const card = el('div', `bk-now t-${now.tone}${now.tab ? ' go' : ''}`, undefined, this.homeEl);
    const text = el('div', 'tx', undefined, card);
    if (now.avatar && pastePhoto(text, now.avatar, 'thumb')) text.classList.add('ph');
    const words = el('div', 'w', undefined, text);
    el('span', 'kicker', now.kicker, words);
    el('b', 'tt', now.title, words);
    if (now.sub) el('small', '', now.sub, words);
    if (now.tab) {
      const tab = now.tab;
      text.setAttribute('role', 'button');
      text.addEventListener('click', (e) => {
        e.stopPropagation();
        this.pages.sound('tick');
        this.openSection(tab, now.focusId);
      });
    }
    if (now.actions.length) {
      const row = el('div', 'acts', undefined, card);
      now.actions.forEach((a, k) => {
        const b = this.button(a, k === 0, row);
        this.homeItems.push(b);
      });
    }
  }

  private runTile(t: BookTile): void {
    if (t.run) {
      this.pages.sound('confirm');
      t.run();
    } else if (t.tab) {
      this.pages.sound('tick');
      this.openSection(t.tab);
    }
  }

  /** A tile or the slip opened a section: back comes back here. */
  openSection(tab: BookTab, focusId?: string): void {
    this.show(tab, true, focusId);
    this.fromHome = true;
  }

  private focusHome(i: number, scroll = true): void {
    this.homeIndex = Math.max(0, Math.min(this.homeItems.length - 1, i));
    this.homeItems.forEach((e, k) => toggle(e, 'focus', k === this.homeIndex));
    if (scroll) this.homeItems[this.homeIndex]?.scrollIntoView?.({ block: 'nearest' });
  }

  /** Arrow keys on HOME: ← → step through in reading order; ↑ ↓ go to the nearest thing in the row above / below. */
  private moveHome(a: 'up' | 'down' | 'left' | 'right'): void {
    this.focusHome(homeNav(this.homeItems.map((e) => e.getBoundingClientRect()), this.homeIndex, a));
  }

  // ------------------------------------------------------------------ a section

  /** Turn to a section (keeping the chosen entry when just refreshing), optionally straight to one entry by id. */
  show(tab: BookTab, reset = false, focusId?: string): void {
    if (!this.pages.tabs().includes(tab)) tab = this.pages.tabs()[0] ?? 'jobs';
    if (tab !== this.tab || this.view === 'home') reset = true;
    // Turned to from the game (a message's button, a hook) rather than from a tile: back shuts the book.
    if (this.view === 'home') this.fromHome = false;
    this.tab = tab;
    this.view = tab;
    this.setChrome();
    const page = this.pages.page(tab);
    this.titleEl.textContent = '';
    html('span', 'ic', BOOK_ICONS[TAB_ICONS[tab]], this.titleEl);
    const words = el('div', 'w', undefined, this.titleEl);
    el('h2', '', page.heading, words);
    if (page.lede) el('small', 'lede', page.lede, words);
    this.leftEl.textContent = '';
    this.entries = page.entries;
    const ul = el('ul', 'bk-list', undefined, this.leftEl);
    let section: string | undefined;
    // Photos only where somebody on the page has one; then every line keeps the same text column.
    const photos = this.entries.some((e) => portraitId(e.avatar));
    this.listEls = this.entries.map((en, i) => {
      if (en.section && en.section !== section) {
        section = en.section;
        const h = el('li', 'bk-sec', undefined, ul);
        h.setAttribute('aria-hidden', 'true');
        el('span', '', en.section, h);
      }
      const li = el('li', en.mark ? `m-${en.mark}` : '', undefined, ul);
      li.dataset.entry = en.id;
      if (en.key) el('kbd', '', en.key, li);
      if (photos && !(en.avatar && pastePhoto(li, en.avatar, 'thumb'))) el('span', 'bh-photo-none', undefined, li);
      const t = el('span', 'tt', undefined, li);
      el('b', '', en.title, t);
      if (en.sub) {
        const sub = el('small', '', undefined, t);
        if (en.icon) applyIcon(el('i', 'bh-ic-tag', undefined, sub), en.icon);
        sub.append(en.sub);
      }
      if (en.mark) el('i', `stamp ${en.mark}`, MARK_TEXT[en.mark], li);
      li.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.index === i) {
          // A second tap on the chosen line does its main thing (where there is one).
          const a = this.entries[i]?.actions?.[0];
          if (a && this.pages.touch()) {
            this.pages.sound('confirm');
            a.run();
          }
          return;
        }
        this.pages.sound('tick');
        this.select(i);
      });
      return li;
    });
    if (!this.entries.length) el('p', 'empty', page.empty ?? 'Nothing written here yet.', this.leftEl);
    // Straight to one entry (a contact's "read the last message"): nothing else gets chosen, so no other
    // message is marked read on the way.
    const wanted = focusId ? this.entries.findIndex((e) => e.id === focusId) : -1;
    if (wanted >= 0) this.index = wanted;
    else if (reset || this.index >= this.entries.length) this.index = 0;
    this.leftEl.scrollTop = 0;
    this.select(this.index);
    this.cueLeft();
  }

  private select(i: number): void {
    this.index = Math.max(0, Math.min(this.entries.length - 1, i));
    this.listEls.forEach((li, k) => toggle(li, 'focus', k === this.index));
    this.listEls[this.index]?.scrollIntoView?.({ block: 'nearest' });
    const en = this.entries[this.index];
    if (en?.onSelect) {
      en.onSelect();
      // Reading a message changes its stamp.
      const li = this.listEls[this.index];
      if (li && en.mark === 'new') {
        li.classList.remove('m-new');
        li.querySelector('.stamp')?.remove();
        en.mark = undefined;
      }
    }
    this.renderDetail();
  }

  private button(a: BookButton, primary: boolean, row: HTMLElement): HTMLElement {
    const b = el('button', `bk-btn${primary ? ' primary' : ''}${a.cls ? ` ${a.cls}` : ''}`, undefined, row);
    b.type = 'button';
    if (a.key && !this.pages.touch()) html('kbd', '', esc(a.key), b);
    const l = el('span', 'l', undefined, b);
    el('span', '', a.label, l);
    if (a.sub) el('small', '', a.sub, l);
    for (const [dk, dv] of Object.entries(a.data ?? {})) b.dataset[dk] = dv;
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      this.pages.sound('confirm');
      a.run();
    });
    return b;
  }

  private renderDetail(): void {
    const r = this.rightEl;
    r.textContent = '';
    r.scrollTop = 0;
    const en = this.entries[this.index];
    if (!en) {
      const p = this.pages.page(this.tab);
      el('p', 'empty', p.empty ?? 'Nothing written here yet.', r);
      this.cueRight();
      return;
    }
    if (en.photo) pastePhoto(r, en.photo.name, en.photo.size);
    if (en.kicker) {
      const k = el('div', 'kicker', undefined, r);
      if (en.icon) applyIcon(el('i', 'bh-ic-tag', undefined, k), en.icon);
      k.append(en.kicker);
    }
    el('h3', '', en.heading ?? en.title, r);
    if (en.ladder) renderLadder(r, en.ladder);
    if (en.render) {
      en.render(r);
      this.cueRight();
      return;
    }
    for (const para of en.body ?? []) el('p', '', para, r);
    if (en.meta) el('p', 'meta', en.meta, r);
    if (en.actions?.length) {
      // Pinned to the foot of the page (position: sticky): the text scrolls under it, the button never
      // leaves the screen on a short phone page.
      const row = el('div', 'bk-actions', undefined, r);
      en.actions.forEach((a, k) => this.button(a, k === 0, row));
    }
    this.cueRight();
  }

  /** Re-draw what is showing (new message, online state changed) without losing the reader's place. */
  refresh(): void {
    if (this.view === 'home') {
      this.renderHome();
      return;
    }
    const id = this.entries[this.index]?.id;
    const fromHome = this.fromHome;
    this.show(this.tab);
    this.fromHome = fromHome;
    const i = this.entries.findIndex((e) => e.id === id);
    if (i >= 0 && i !== this.index) this.select(i);
  }

  // ------------------------------------------------------------------ keys and pad

  onMenuKey(e: KeyboardEvent): boolean {
    if (e.ctrlKey || e.altKey || e.metaKey) return false;
    if (e.code === 'KeyI') {
      if (e.repeat) return true;
      this.pages.sound('back');
      this.pages.close();
      return true;
    }
    const digit = /^(?:Digit|Numpad)([1-9])$/.exec(e.code);
    if (digit && this.view === 'home') {
      const tile = this.tileEls[Number(digit[1]) - 1];
      if (tile) tile.click();
      else this.pages.sound('denied');
      return true;
    }
    return false;
  }

  onNav(a: NavAction): boolean {
    this.root.classList.add('kb'); // show the focus ring once the keys / pad are in use
    if (a === 'back') {
      this.pages.sound('back');
      if (this.view !== 'home' && this.fromHome) this.goHome();
      else this.pages.close();
      return true;
    }
    if (this.view === 'home') {
      if (a === 'confirm') {
        const item = this.homeItems[this.homeIndex];
        if (item) item.click();
        else this.pages.sound('denied');
      } else {
        this.pages.sound('tick');
        this.moveHome(a);
      }
      return true;
    }
    if (a === 'left' || a === 'right') {
      const tabs = this.pages.tabs();
      const i = tabs.indexOf(this.tab);
      const next = tabs[(i + (a === 'left' ? -1 : 1) + tabs.length) % tabs.length]!;
      this.pages.sound('tick');
      const fromHome = this.fromHome;
      this.show(next, true);
      this.fromHome = fromHome;
      return true;
    }
    if (a === 'up' || a === 'down') {
      if (this.entries.length > 1) {
        this.pages.sound('tick');
        this.select((this.index + (a === 'up' ? -1 : 1) + this.entries.length) % this.entries.length);
      }
      return true;
    }
    if (a === 'confirm') {
      const act = this.entries[this.index]?.actions?.[0];
      if (act) {
        this.pages.sound('confirm');
        act.run();
      } else this.pages.sound('denied');
      return true;
    }
    return true;
  }

  dispose(): void {
    this.root.remove();
  }
}
